import { useEffect, useRef, useState } from 'react';
import { BrandAssembly, type BrandAssemblyHandle } from '../brand/BrandAssembly';
import { SITE, sectionId } from '../content/site';
import { ROUTES, useLocale } from '../i18n/locale';
import { LanguageSwitch } from './LanguageSwitch';

const SEEN = 'cx-brand-seen';

/**
 * Navigation that follows the page: transparent over the globe, a solid bar once scrolled, and a
 * light or dark tone taken from the section beneath it (sections declare data-tone).
 */
export function SiteHeader() {
  const { locale, page } = useLocale(); const copy = SITE[locale];
  const anchor = (key: Parameters<typeof sectionId>[0]) => `${page === 'home' ? '' : ROUTES.home[locale]}#${sectionId(key, locale)}`;
  const mark = useRef<BrandAssemblyHandle>(null);
  const header = useRef<HTMLElement>(null);
  const [open, setOpen] = useState(false);
  const menuButton = useRef<HTMLButtonElement>(null);

  useEffect(() => {
    let seen = true;
    try { seen = sessionStorage.getItem(SEEN) === '1'; sessionStorage.setItem(SEEN, '1'); } catch { /* storage unavailable */ }
    if (seen) mark.current?.setProgress(1); else void mark.current?.play('ASSEMBLE');
    let frame = 0;
    const update = () => {
      frame = 0; const element = header.current; if (!element) return;
      element.dataset.scrolled = String(window.scrollY > 24);
      const probe = element.offsetHeight / 2; let tone = 'dark';
      for (const section of document.querySelectorAll<HTMLElement>('[data-tone]')) {
        const rect = section.getBoundingClientRect(); if (rect.top <= probe && rect.bottom > probe) { tone = section.dataset.tone ?? 'dark'; }
      }
      element.dataset.tone = tone;
    };
    const schedule = () => { if (!frame) frame = requestAnimationFrame(update); };
    update(); window.addEventListener('scroll', schedule, { passive: true }); window.addEventListener('resize', schedule, { passive: true });
    return () => { window.removeEventListener('scroll', schedule); window.removeEventListener('resize', schedule); if (frame) cancelAnimationFrame(frame); };
  }, []);

  useEffect(() => {
    if (!open) return;
    document.documentElement.dataset.menu = 'open';
    const close = (event: KeyboardEvent) => { if (event.key === 'Escape') { setOpen(false); menuButton.current?.focus(); } };
    window.addEventListener('keydown', close);
    return () => { delete document.documentElement.dataset.menu; window.removeEventListener('keydown', close); };
  }, [open]);

  return (
    <header ref={header} className="site-header" data-scrolled="false" data-tone="dark">
      <div className="site-header__inner">
        <a className="site-header__brand" href={ROUTES.home[locale]} aria-label={copy.homeLabel}>
          <BrandAssembly ref={mark} tone="dark" showWordmark={false} initial={0} label="" className="site-header__mark" />
          <svg className="site-header__word" viewBox="0 0 520 64" aria-hidden="true">
            <text x="0" y="50" fontFamily="'Montserrat Variable', Montserrat, sans-serif" fontWeight={780} fontSize="62" textLength="516" lengthAdjust="spacing">
              <tspan className="site-header__word-a">COLES</tspan><tspan fill="#00b4db">COMEX</tspan>
            </text>
          </svg>
        </a>
        <nav className="site-header__nav" aria-label={copy.menu.label}>
          <ul role="list">
            {copy.nav.map(item => <li key={item.key}><a href={anchor(item.key)}>{item.label}</a></li>)}
          </ul>
        </nav>
        <LanguageSwitch className="site-header__lang" />
        <a className="cta cta--primary cta--compact site-header__cta" href={ROUTES.intake[locale]} aria-current={page === 'intake' ? 'page' : undefined}>{copy.start}</a>
        <button ref={menuButton} type="button" className="site-header__menu" aria-expanded={open} aria-controls="site-menu" onClick={() => setOpen(value => !value)}>
          <span className="visually-hidden">{open ? copy.menu.close : copy.menu.open}</span>
          <span aria-hidden="true" className="site-header__menu-icon" />
        </button>
      </div>
      <div id="site-menu" className="site-menu" hidden={!open}>
        <ul role="list">
          {copy.nav.map(item => <li key={item.key}><a href={anchor(item.key)} onClick={() => setOpen(false)}>{item.label}</a></li>)}
        </ul>
        <a className="cta cta--primary" href={ROUTES.intake[locale]} onClick={() => setOpen(false)}>{copy.start}</a>
        <LanguageSwitch className="site-menu__lang" />
      </div>
    </header>
  );
}
